/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { gql, useQuery } from '@apollo/client';
import Confetti from 'react-confetti';
import { fadeIn } from 'react-animations';
import { ProgressBar } from './ProgressBar';
import WestlandLogo from './WestlandLogo';
import ResetIcon from './ResetIcon';

interface KnowledgeQuizProps {
    trainingId: any;
}

interface QuizProps {
    questions: any;
}

interface StyledImageProps {
    transition?: boolean;
}

interface StyledLabelProps {
    selected: boolean;
    incorrect?: boolean;
    correct?: boolean;
    disabled?: boolean;
    checkbox?: boolean;
    transition?: boolean;
}

interface StyledButtonProps {
    arrow?: boolean;
    transparent?: boolean;
}

interface StyledKnowledgeQuizProps {
    isComplete?: boolean;
}

const fadeInAnimation = keyframes`${fadeIn}`;

const StyledKnowledgeQuizWrapper = styled.div`
    ${({ theme }) => css`
        color: ${theme.colors.copy};
        margin-top: ${theme.space.lg};
        font-size: 16px;
        line-height: 24px;
    `}
`;

const StyledIntroduction = styled.p`
    ${({ theme }) => css`
        margin-bottom: ${theme.space.md}!important;
        max-width: 750px;

        @media (min-width: ${theme.breakpoints.sm}px) {
            margin-bottom: ${theme.space.lg}!important;
        }
    `}
`;

const StyledKnowledgeQuiz = styled.div<StyledKnowledgeQuizProps>`
    ${({ theme, isComplete }) => css`
        background: ${isComplete ? theme.colors.darkGreen : '#fff'};
        color: ${isComplete ? '#fff' : theme.colors.copy};
        padding: ${theme.space.md};
        border-radius: ${theme.borderRadius.sm};
        position: relative;
        overflow: hidden;
        @media (min-width: ${theme.breakpoints.sm}px) {
            padding: ${theme.space.lg};
        }
    `}
`;

const StyledQuizBody = styled.div`
    ${({ theme }) => css`
        @media (min-width: ${theme.breakpoints.sm}px) {
            display: flex;

            form {
                flex-grow: 1;
            }
        }
    `}
`;

const StyledImage = styled.div<StyledImageProps>`
    ${({ theme, transition }) => css`
        margin-bottom: ${theme.space.md};
        flex-shrink: 0;

        ${transition &&
        css`
            animation: 2s ${fadeInAnimation};
        `}

        img {
            border-radius: ${theme.borderRadius.sm};
            width: 100%;
        }

        @media (min-width: ${theme.breakpoints.sm}px) {
            width: 400px;
            margin-bottom: 0;
            margin-right: ${theme.space.lg};
        }
    `}
`;

const StyledFieldset = styled.fieldset`
    ${({ theme }) => css`
        padding: 0;
        border: none;

        legend {
            font-size: 24px;
            font-weight: bold;
            letter-spacing: -0.24px;
            line-height: 32px;
        }

        ul {
            list-style: none;
            margin: ${theme.space.md} 0 0 0;
            padding: 0;

            li + li {
                margin-top: ${theme.space.xs};
            }
        }

        @media (min-width: ${theme.breakpoints.sm}px) {
            legend {
                max-width: 580px;
            }
        }
    `}
`;

const StyledInput = styled.input`
    ${({ theme }) => css`
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    `}
`;

const StyledLabel = styled.label<StyledLabelProps>`
    ${({ theme, selected, incorrect, disabled, correct, checkbox, transition }) => css`
        border: solid 2px ${theme.colors.midGrey};
        display: flex;
        align-items: center;
        border-radius: ${theme.borderRadius.sm};
        background-color: #fff;
        font-size: 16px;
        line-height: 24px;
        padding: 10px ${theme.space.sm};
        position: relative;
        overflow: hidden;

        &:before {
            content: '';
            display: block;
            flex-shrink: 0;
            width: ${theme.space.md};
            height: ${theme.space.md};
            border: solid 2px ${theme.colors.midGrey};
            border-radius: 50%;
            margin-right: ${theme.space.sm};
            background: #fff;
            box-sizing: border-box;
        }

        ${transition &&
        css`
            animation: 1s ${fadeInAnimation};
        `}

        ${checkbox &&
        css`
            &:before {
                border-radius: ${theme.space.xxs};
            }
        `}

        ${!disabled &&
        css`
            &:hover {
                background-color: ${theme.colors.lightGrey};
                cursor: pointer;
            }
        `}

        // States

        // Hidden input focus

        ${StyledInput}:focus ~ & {
            background-color: ${theme.colors.lightGreen};
            border-color: ${theme.colors.green};

            &:before {
                border: solid 6px ${theme.colors.green};
            }
        }

        // Selected

        ${selected &&
        css`
            background-color: ${theme.colors.lightGreen};
            border-color: ${theme.colors.green};

            &:before {
                border: solid 6px ${theme.colors.green};
            }
        `}

        // Incorrect

        ${incorrect &&
        css`
            background-color: ${theme.colors.lightRed};
            border-color: ${theme.colors.lightRed};
            color: ${theme.colors.red};

            &:before {
                content: '';
                background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='18px' height='18px' viewBox='0 0 18 18' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EIcon/Cross%3C/title%3E%3Cg id='Westland-Training-Academy' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Topic-Details---Quiz-Answer---Incorrect' transform='translate(-683.000000, -927.000000)'%3E%3Crect fill='%23FFFFFF' x='0' y='0' width='1448' height='1616'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%23EEEEEE' x='0' y='344' width='1448' height='1072'%3E%3C/rect%3E%3Crect id='Photo-MASK' fill='%23FFFFFF' x='160' y='648' width='1128' height='680' rx='4'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%23CD0A0A' opacity='0.24' x='664' y='912' width='576' height='48' rx='4'%3E%3C/rect%3E%3Cg id='Icon/Cross' transform='translate(680.000000, 924.000000)'%3E%3Crect id='Background' x='0' y='0' width='24' height='24'%3E%3C/rect%3E%3Cpath d='M15.4118182,12 L20.5290341,6.88278409 C21.1569886,6.25482955 21.1569886,5.23670455 20.5290341,4.60823864 L19.3917614,3.47096591 C18.7638068,2.84301136 17.7456818,2.84301136 17.1172159,3.47096591 L12,8.58818182 L6.88278409,3.47096591 C6.25482955,2.84301136 5.23670455,2.84301136 4.60823864,3.47096591 L3.47096591,4.60823864 C2.84301136,5.23619318 2.84301136,6.25431818 3.47096591,6.88278409 L8.58818182,12 L3.47096591,17.1172159 C2.84301136,17.7451705 2.84301136,18.7632955 3.47096591,19.3917614 L4.60823864,20.5290341 C5.23619318,21.1569886 6.25482955,21.1569886 6.88278409,20.5290341 L12,15.4118182 L17.1172159,20.5290341 C17.7451705,21.1569886 18.7638068,21.1569886 19.3917614,20.5290341 L20.5290341,19.3917614 C21.1569886,18.7638068 21.1569886,17.7456818 20.5290341,17.1172159 L15.4118182,12 Z' id='Cross' fill='%23CD0A0A'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                width: 18px;
                height: 18px;
                border-radius: 0;
                border: none;
            }
        `}

        // Correct

        ${correct &&
        css`
            background-color: ${theme.colors.green};
            border-color: ${theme.colors.green};
            color: ${theme.colors.darkGreen};

            &:before {
                content: '';
                background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='24px' height='18px' viewBox='0 0 24 18' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EIcon/Tick%3C/title%3E%3Cg id='Westland-Training-Academy' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Topic-Details---Quiz-Answer---Incorrect' transform='translate(-680.000000, -1040.000000)'%3E%3Crect fill='%23FFFFFF' x='0' y='0' width='1448' height='1616'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%23EEEEEE' x='0' y='344' width='1448' height='1072'%3E%3C/rect%3E%3Crect id='Photo-MASK' fill='%23FFFFFF' x='160' y='648' width='1128' height='680' rx='4'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%238DC63F' x='664' y='1024' width='576' height='48' rx='4'%3E%3C/rect%3E%3Cg id='Icon/Tick' transform='translate(680.000000, 1037.000000)'%3E%3Crect id='Background' x='0' y='0' width='24' height='24'%3E%3C/rect%3E%3Cpath d='M8.15146875,20.5501875 L0.35146875,12.7501875 C-0.117140625,12.2815781 -0.117140625,11.5217813 0.35146875,11.053125 L2.04848437,9.3560625 C2.51709375,8.88740625 3.2769375,8.88740625 3.74554687,9.3560625 L9,14.6104688 L20.2544531,3.3560625 C20.7230625,2.88745313 21.4829062,2.88745313 21.9515156,3.3560625 L23.6485312,5.053125 C24.1171406,5.52173438 24.1171406,6.28153125 23.6485312,6.7501875 L9.84853125,20.5502344 C9.379875,21.0188438 8.62007812,21.0188438 8.15146875,20.5501875 L8.15146875,20.5501875 Z' id='Tick' fill='%23034725'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                width: 24px;
                height: 18px;
                border-radius: 0;
                border: none;
            }
        `}
    `}
`;

const StyledFeedback = styled.p`
    ${({ theme }) => css`
        margin: ${theme.space.md} 0 0 0;
    `}
`;

const StyledQuizActions = styled.div`
    ${({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: ${theme.space.md};
        margin-top: ${theme.space.xl};
        border-top: solid 2px ${theme.colors.lightGrey};

        @media (min-width: ${theme.breakpoints.sm}px) {
        }
    `}
`;

const StyledButton = styled.button<StyledButtonProps>`
    ${({ theme, arrow, transparent }) => css`
        display: flex;
        align-items: center;
        background-color: ${theme.colors.brand};
        color: #fff;
        border: none;
        font-weight: bold;
        border-radius: ${theme.borderRadius.sm};
        font-size: 16px;
        font-weight: bold;
        letter-spacing: -0.16px;
        line-height: 24px;
        padding: ${theme.space.sm} ${theme.space.md};
        transition-property: all;
        transition-duration: 0.25s;
        transition-timing-function: ease-in;

        @media (min-width: ${theme.breakpoints.sm}px) {
            padding: ${theme.space.sm} ${theme.space.lg};
        }

        svg {
            margin-right: ${theme.space.sm};
        }

        &:disabled {
            background-color: #cccccc;
            cursor: not-allowed;
        }

        &:enabled:hover {
            opacity: 0.9;
            text-decoration: underline;
            cursor: pointer;
        }

        ${arrow &&
        css`
            &:after {
                content: '';
                width: 6px;
                height: 8px;
                margin-left: ${theme.space.xs};
                background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='6px' height='8px' viewBox='0 0 6 8' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EIcon/Caret%3C/title%3E%3Cg id='Westland-Training-Academy' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Topic-Details---Quiz-Answer---Incorrect' transform='translate(-1189.000000, -1248.000000)'%3E%3Crect fill='%23FFFFFF' x='0' y='0' width='1448' height='1616'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%23EEEEEE' x='0' y='344' width='1448' height='1072'%3E%3C/rect%3E%3Crect id='Photo-MASK' fill='%23FFFFFF' x='160' y='648' width='1128' height='680' rx='4'%3E%3C/rect%3E%3Crect id='Rectangle' fill='%23046A38' x='1024' y='1224' width='216' height='56' rx='4'%3E%3C/rect%3E%3Cg id='Icon/Caret' transform='translate(1188.000000, 1248.000000)'%3E%3Crect id='Background' x='0' y='0' width='8' height='8'%3E%3C/rect%3E%3Cpath d='M1.66666667,7.46092787 L1.66666667,0.539060183 C1.66666667,0.0597662253 2.24616098,-0.180257346 2.58506684,0.158648512 L6.04600068,3.61958236 C6.2560852,3.82966687 6.2560852,4.17032119 6.04600068,4.3804057 L2.58506684,7.84133954 C2.24616098,8.1802723 1.66666667,7.94022183 1.66666667,7.46092787 Z' id='Caret' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
        `}

        ${transparent &&
        css`
            background: none;
            border: none;
            color: ${theme.colors.copy};
            font-weight: normal;
            padding: 0;

            &:hover {
                color: ${theme.colors.brand};
                background: none;
            }
        `}
    `}
`;

const StyledCompleteScreen = styled.div`
    ${({ theme }) => css`
        @media (min-width: ${theme.breakpoints.sm}px) {
            display: flex;
            justify-content: space-between;
        }
    `}
`;

const StyledCompleteScreenCopy = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: ${theme.space.md} 0 ${theme.space.lg} 0;
        animation: 1s ${fadeInAnimation};

        h2,
        p {
            font-family: ${theme.typography.fontFamily};
        }

        h2 {
            color: ${theme.colors.green};
            font-size: 48px;
            line-height: 40px;
            margin: 0 0 ${theme.space.md} 0;
        }

        p {
            font-size: 104px;
            line-height: 88px;
            margin: 0;
            color: #fff;

            strong {
                margin-top: ${theme.space.sm};
                display: block;
                color: ${theme.colors.green};
            }
        }

        @media (min-width: ${theme.breakpoints.sm}px) {
            padding: ${theme.space.xl} ${theme.space.lg};
        }
    `}
`;

const StyledCompleteScreenActions = styled.div`
    ${({ theme }) => css`
        margin-bottom: ${theme.space.md};

        @media (min-width: ${theme.breakpoints.sm}px) {
            margin: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }
    `}
`;

const StyledLogo = styled.div`
    ${({ theme }) => css`
        display: none;
        @media (min-width: ${theme.breakpoints.sm}px) {
            display: flex;
            align-items: center;
            justify-content: flex-end;

            p {
                margin: 0px 0px 0px 16px;
                font-family: Veneer;
                font-size: 28px;
                line-height: 26px;
                width: 89px;
                color: #fff;
            }
        }
    `}
`;

const GET_QUESTIONS = gql`
    query Question($trainingId: ID!) {
        trainingArticle(id: $trainingId, idType: DATABASE_ID) {
            id
            title
            KnowledgeQuiz {
                introduction
                questions {
                    image {
                        sourceUrl(size: QUIZ)
                        altText
                    }
                    content {
                        question
                        answers {
                            answer
                            correctAnswer
                        }
                        feedback
                    }
                }
            }
        }
    }
`;

const Quiz: FC<QuizProps> = ({ questions }) => {
    // Set-up state for app

    const [score, setScore] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [currentSelectedAnswers, setCurrentSelectedAnswers] = useState([]) as any;
    const [displayingFeedback, setDisplayingFeedback] = useState(false);
    const [quizComplete, setQuizComplete] = useState(false);
    const [transition, setTransition] = useState(true);

    // Get current question, answers and content

    const { question: activeQuestion, answers: activeAnswers, feedback: activeFeedback } = questions[
        currentQuestion
    ]?.content;

    // Get current set of correct answers

    const correctAnswers = [] as any;
    activeAnswers
        .filter((answer) => answer.correctAnswer === true)
        .forEach((answer) => correctAnswers.push(answer.answer.toLowerCase().replace(/\s/g, '')));

    // Determine if current question is multi-choice

    const multiChoice = correctAnswers.length > 1;

    // Handle Selecting answer

    const handleSelectingAnswer = (e) => {
        const answerId = e.target.value;

        // Check if selected answer is already selected...

        if (currentSelectedAnswers.includes(answerId)) {
            // Remove it from selected list...

            const filteredSelectAnswers = currentSelectedAnswers.filter(function (answer, index, arr) {
                return answer !== answerId;
            });

            setCurrentSelectedAnswers([...filteredSelectAnswers]);
        } else if (correctAnswers.length > 1) {
            // if multi-choice add to array...

            setCurrentSelectedAnswers([...currentSelectedAnswers, answerId]);
        } else {
            // If single choice...

            setCurrentSelectedAnswers([answerId]);
        }
    };

    // Handle checking answers

    const checkAnswer = () => {
        setDisplayingFeedback(true);

        // Sort the selected and correct arrays and join them to compare...

        const stringifiedSelectedAnswers = currentSelectedAnswers.sort().join('-');
        const stringifiedCorrectAnswers = correctAnswers.sort().join('-');

        if (stringifiedSelectedAnswers === stringifiedCorrectAnswers) {
            setScore(score + 1);
        }
    };

    // Handle navigating to next question

    const nextQuestion = () => {
        // Set current question to next one

        setCurrentQuestion(currentQuestion + 1);

        // Reset question state
        setDisplayingFeedback(false);
        setCurrentSelectedAnswers([]);
    };

    // Handle viewing score

    const viewScore = () => {
        setQuizComplete(true);
    };

    // Reset quiz

    const resetQuiz = () => {
        setQuizComplete(false);
        setDisplayingFeedback(false);
        setCurrentSelectedAnswers([]);
        setCurrentQuestion(0);
        setScore(0);
    };

    // Check for changes in question - handle transition state
    useEffect(() => {
        setTransition(true);
        const timer = setTimeout(() => {
            setTransition(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [currentQuestion]);

    return (
        <StyledKnowledgeQuiz isComplete={quizComplete}>
            {currentQuestion + 1 <= questions.length && !quizComplete && (
                <>
                    <ProgressBar currentQuestion={currentQuestion + 1} noOfQuestions={questions.length} />
                    <StyledQuizBody>
                        {questions[currentQuestion]?.image && (
                            <StyledImage transition={transition}>
                                <img
                                    src={questions[currentQuestion]?.image.sourceUrl}
                                    alt={`${questions[currentQuestion]?.image.altText}`}
                                />
                            </StyledImage>
                        )}

                        <form>
                            <StyledFieldset>
                                <legend>{activeQuestion}</legend>
                                <ul>
                                    {activeAnswers.map((item, index) => {
                                        const { answer } = item;
                                        const name = activeQuestion.toLowerCase().replace(/\s/g, '');
                                        const id = answer.toLowerCase().replace(/\s/g, '');
                                        const incorrect =
                                            displayingFeedback &&
                                            !correctAnswers.includes(id) &&
                                            currentSelectedAnswers.includes(id);
                                        const correct = displayingFeedback && correctAnswers.includes(id);
                                        return (
                                            <li key={index}>
                                                <StyledInput
                                                    id={id}
                                                    type={multiChoice ? 'checkbox' : 'radio'}
                                                    value={id}
                                                    onChange={handleSelectingAnswer}
                                                    checked={currentSelectedAnswers.includes(id)}
                                                    disabled={displayingFeedback}
                                                    name={name}
                                                />
                                                <StyledLabel
                                                    htmlFor={id}
                                                    selected={
                                                        currentSelectedAnswers.includes(id) && !displayingFeedback
                                                    }
                                                    disabled={
                                                        displayingFeedback ||
                                                        (!multiChoice && currentSelectedAnswers.includes(id))
                                                    }
                                                    incorrect={incorrect}
                                                    correct={correct}
                                                    checkbox={multiChoice}
                                                    transition={transition}
                                                >
                                                    {/* {correct && currentSelectedAnswers.includes(id) && (
                                                        <Confetti
                                                            recycle={false}
                                                            numberOfPieces={500}
                                                            tweenDuration={5000}
                                                        />
                                                    )} */}
                                                    {answer}
                                                </StyledLabel>
                                            </li>
                                        );
                                    })}
                                </ul>

                                {displayingFeedback && <StyledFeedback>{activeFeedback}</StyledFeedback>}
                            </StyledFieldset>
                        </form>
                    </StyledQuizBody>
                    <StyledQuizActions>
                        {currentQuestion > 0 ? (
                            <StyledButton type="button" onClick={resetQuiz} transparent>
                                <ResetIcon /> Start Quiz Again
                            </StyledButton>
                        ) : (
                            <span />
                        )}

                        {!displayingFeedback && (
                            <StyledButton
                                type="button"
                                onClick={checkAnswer}
                                disabled={currentSelectedAnswers.length === 0}
                            >
                                Check Answers
                            </StyledButton>
                        )}

                        {displayingFeedback && currentQuestion + 1 !== questions.length && (
                            <StyledButton type="button" onClick={nextQuestion} arrow>
                                Next question
                            </StyledButton>
                        )}

                        {displayingFeedback && currentQuestion + 1 === questions.length && (
                            <StyledButton type="button" onClick={viewScore} arrow>
                                View score
                            </StyledButton>
                        )}
                    </StyledQuizActions>
                </>
            )}
            {quizComplete && (
                <>
                    <StyledCompleteScreen>
                        <StyledCompleteScreenCopy>
                            <h2>Knowledge quiz</h2>
                            <p>
                                You
                                <br />
                                scored{' '}
                                <strong>
                                    {score}/{questions.length}
                                </strong>
                            </p>
                        </StyledCompleteScreenCopy>
                        <StyledCompleteScreenActions>
                            <StyledLogo>
                                <WestlandLogo />
                                <p>Training Academy</p>
                            </StyledLogo>
                            <StyledButton type="button" onClick={resetQuiz}>
                                <ResetIcon /> Start Quiz Again
                            </StyledButton>
                        </StyledCompleteScreenActions>
                    </StyledCompleteScreen>
                    {score === questions.length && (
                        <Confetti recycle={false} numberOfPieces={1200} tweenDuration={20000} />
                    )}
                </>
            )}
        </StyledKnowledgeQuiz>
    );
};

export const KnowledgeQuiz: FC<KnowledgeQuizProps> = ({ trainingId }) => {
    const { loading: questionsLoading, error: questionsError, data: questionsData } = useQuery(GET_QUESTIONS, {
        variables: { trainingId },
    });

    // Handle loading
    if (questionsLoading) return <p>Loading...</p>;

    // Handle errors
    if (questionsError) return <p>Error! {questionsError.message}</p>;

    const { introduction, questions } = questionsData?.trainingArticle?.KnowledgeQuiz;

    return (
        <StyledKnowledgeQuizWrapper>
            <h3>Test your product knowledge</h3>
            {introduction && <StyledIntroduction>{introduction}</StyledIntroduction>}
            {questions && questions.length >= 1 && <Quiz questions={questions} />}
        </StyledKnowledgeQuizWrapper>
    );
};
